import { gql, useQuery } from '@nerdwallet/apollo';
// eslint-disable-next-line lodash/import-scope
import get from 'lodash/get';
import { useMemo } from 'react';

export function isValidDate(dateStr) {
  return !Number.isNaN(new Date(dateStr).getDate());
}

export function replaceInterpolatedValues(text = '', arr = []) {
  /** use:
     replaceInterpolatedValues('NerdWallet strives to {value1} accurate {value2} and up to date.', ['replaced2', 'replaced1'])
     returns 'NerdWallet strives to replaced2 accurate replaced1 and up to date.'
   */

  // Regular expression to match everything inside curly braces
  const regex = /\{\{.*?\}\}/g;
  // Replace each placeholder with the corresponding value from the array
  let index = 0;
  return text.replace(regex, () => {
    const interpolatedOcurency = arr[index] || '';
    index += 1;
    return interpolatedOcurency;
  });
}

export function getInterpolatedValues(text = '') {
  /** use:
     getInterpolatedValues('NerdWallet strives to {value1} accurate {value2} and up to date.')
     returns ['value1', 'value2']
   */
  const regex = /\{\{(.*?)\}\}/g; // extracts the value between "{" and "}"
  const matches = [];

  let match = regex.exec(text);
  while (match !== null) {
    matches.push(match[1]);
    match = regex.exec(text);
  }

  return matches;
}

export function parseDottedStringToJsonString(str) {
  const parts = str.split('.');
  let result = '';

  parts.forEach((part, index) => {
    // Add curly braces for all except the last part
    if (index < parts.length - 1) {
      result += `${''.repeat(index)}${part}{`;
    } else {
      result += `${''.repeat(index)}${part}`;
    }
  });

  // Closing curly braces for all but the last part
  for (let i = parts.length - 2; i >= 0; i -= 1) {
    result += `${''.repeat(i)}}`;
  }
  return result;
}

export function parseToGraphQLQuery(str) {
  const formattedStr = str.trim();
  const graphQLQuery = `${formattedStr
    .split('{')
    .join('{\n  ')
    .split('}')
    .join('\n  }')}`;
  return graphQLQuery;
}

export function getQuery(queryStr) {
  const jsonString = parseDottedStringToJsonString(queryStr);
  const gqlString = parseToGraphQLQuery(jsonString);
  return gql`
    query Query {
      ${gqlString}
    }
  `;
}

export function getQueries(queryArray = []) {
  if (!queryArray?.length) {
    return '';
  }

  let combinedGraphQlQueries = '';
  queryArray.forEach((item) => {
    const jsonString = parseDottedStringToJsonString(item);
    combinedGraphQlQueries += jsonString;
  });
  const gqlString = parseToGraphQLQuery(combinedGraphQlQueries);
  return gql`
    query Query {
      ${gqlString}
    }
  `;
}

export function getValueFromObject(srcObject = {}, entriesArray = []) {
  const result = [];
  entriesArray.forEach((item) => result.push(get(srcObject, item, '')));
  return result.map((item) => {
    // format the output when is a number
    if (typeof item === 'number') {
      return item.toFixed(2);
    }
    // format the output when is a date
    if (isValidDate(item)) {
      return new Date(item).toLocaleDateString('en-US');
    }
    return item;
  });
}

export function useInterpolatedValue({
  interpolatedValues,
  shouldSkipQuery,
  text,
}) {
  const skip = useMemo(() => shouldSkipQuery, [shouldSkipQuery]);
  const query = useMemo(() => {
    if (skip) {
      return gql`
        query Query {
          treasuryAccountMarketing {
            yieldToMaturityPercentage
          }
        }
      `;
    }
    return getQueries(interpolatedValues);
  }, [interpolatedValues, skip]);

  const { data } = useQuery(query, {
    skip,
    fetchPolicy: 'network-only', // first request
    nextFetchPolicy: 'cache-first', // each subsequent request
  });

  const retrievedFromObject = getValueFromObject(data, interpolatedValues);
  const processedInterpolatedString = replaceInterpolatedValues(
    text,
    retrievedFromObject
  );
  return { data: data || {}, processedText: processedInterpolatedString };
}
